// F-U: checkbox multi-select used by the Exhibits page filters.
//
// Two layouts over the same checkbox list:
// - "dropdown" (desktop): a button showing the selection, opening a panel.
// - "inline" (phone bottom sheet): a collapsible section, list always in flow.
// Each tick applies immediately (no OK button). Plain elements, no Radix, so
// it behaves the same in jsdom tests as in the browser.

import { useEffect, useMemo, useRef, useState, type FC } from "react";
import { ChevronDown } from "lucide-react";

export interface MultiSelectOption {
  value: string;
  label: string;
  group?: string;
}

export interface MultiSelectFilterProps {
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** Show a search box above the list (long lists: exhibitions, zones). */
  searchable?: boolean;
  layout?: "dropdown" | "inline";
  testId: string;
}

/** "All" · "2025, 2026" · "Design +2" */
export function selectionSummary(
  options: MultiSelectOption[],
  selected: string[],
): string {
  if (!selected.length) return "All";
  const labels = selected.map(
    (v) => options.find((o) => o.value === v)?.label ?? v,
  );
  if (labels.length <= 2) return labels.join(", ");
  return `${labels[0]} +${labels.length - 1}`;
}

const MultiSelectFilter: FC<MultiSelectFilterProps> = ({
  label,
  options,
  selected,
  onChange,
  searchable = false,
  layout = "dropdown",
  testId,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  // Close the dropdown on an outside click or Escape.
  useEffect(() => {
    if (layout !== "dropdown" || !open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [layout, open]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.group ?? "").toLowerCase().includes(q),
    );
  }, [options, query]);

  const toggle = (value: string) =>
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );

  const list = (
    <div className="space-y-2" data-testid={`${testId}-panel`}>
      {searchable && (
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${label.toLowerCase()}…`}
          className="w-full border rounded px-2 py-1 text-sm"
          data-testid={`${testId}-search`}
        />
      )}
      <ul className="max-h-64 overflow-y-auto space-y-0.5">
        {visible.length === 0 && (
          <li className="px-1 py-1 text-sm text-muted-foreground">
            No options
          </li>
        )}
        {visible.map((o, i) => {
          const showGroup =
            o.group !== undefined && o.group !== visible[i - 1]?.group;
          return (
            <li key={o.value}>
              {showGroup && (
                <div className="px-1 pt-2 pb-0.5 text-xs font-medium text-muted-foreground">
                  {o.group}
                </div>
              )}
              <label className="flex items-center gap-2 px-1 py-1 rounded text-sm hover:bg-muted/50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.includes(o.value)}
                  onChange={() => toggle(o.value)}
                  data-testid={`${testId}-option-${o.value}`}
                />
                <span>{o.label}</span>
              </label>
            </li>
          );
        })}
      </ul>
      <div className="flex justify-between border-t pt-2 text-sm">
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground disabled:opacity-50"
          onClick={() => onChange([])}
          disabled={!selected.length}
          data-testid={`${testId}-clear`}
        >
          Clear
        </button>
        <button
          type="button"
          className="text-primary hover:underline"
          onClick={() =>
            onChange([
              ...selected,
              ...visible
                .map((o) => o.value)
                .filter((v) => !selected.includes(v)),
            ])
          }
          data-testid={`${testId}-select-all`}
        >
          Select all
        </button>
      </div>
    </div>
  );

  if (layout === "inline") {
    return (
      <details className="border rounded-md px-3 py-2" data-testid={testId}>
        <summary className="cursor-pointer text-sm font-medium">
          {label}
          {selected.length > 0 && (
            <span className="ml-1 text-muted-foreground">
              ({selected.length})
            </span>
          )}
        </summary>
        <div className="pt-2">{list}</div>
      </details>
    );
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`flex items-center gap-1 h-9 rounded-md border px-2 text-sm bg-background ${
          selected.length ? "border-primary" : "border-input"
        }`}
        data-testid={testId}
      >
        <span className="text-muted-foreground">{label}</span>
        <span className="max-w-[10rem] truncate">
          {selectionSummary(options, selected)}
        </span>
        <ChevronDown className="w-4 h-4 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-64 rounded-md border bg-white p-2 shadow-lg">
          {list}
        </div>
      )}
    </div>
  );
};

export default MultiSelectFilter;
